import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Button } from "@satyrsmc/shared/components";
import { extractEmbedUrlFromHtml, getMapEmbedUrl } from "@/lib/maps";
import { toDateOnly } from "@/lib/date-utils";
import {
  useEventSuspense,
  useBudgetsOptional,
  useScenariosOptional,
  useUpdateEvent,
  useDeleteEvent,
  useCreateEvent,
  useEventScheduleItemCreate,
  useEventScheduleItemUpdate,
  useEventScheduleItemDelete,
  useEventIncidentCreate,
  useEventIncidentUpdate,
  useEventIncidentDelete,
  useEventAssetAdd,
  useEventAssetDelete,
  useEventMilestoneCreate,
  useEventMilestoneUpdate,
  useEventMilestoneDelete,
  useEventMilestoneAddMember,
  useEventMilestoneRemoveMember,
  useEventPackingCategoryCreate,
  useEventPackingItemCreate,
  useEventPackingItemUpdate,
  useEventPackingItemDelete,
  useEventVolunteerCreate,
  useEventVolunteerDelete,
  useEventAssignmentCreate,
  useEventAssignmentDelete,
  useEventAssignmentAddMember,
  useEventAssignmentRemoveMember,
  useEventPhotoAdd,
  useEventPhotoDelete,
  useEventBadgerFlyerUpload,
  useEventBadgerFlyerDelete,
  unwrapSuspenseData,
  useInvalidateQueries,
} from "@/queries/hooks";
import { EventDetailsCard } from "./EventDetailsCard";
import { EventLocationCard } from "./EventLocationCard";
import { EventMilestonesCard } from "./EventMilestonesCard";
import { EventAssignmentsCard } from "./EventAssignmentsCard";
import { EventPackingCard } from "./EventPackingCard";
import { EventVolunteersCard } from "./EventVolunteersCard";
import { EventPhotosCard } from "./EventPhotosCard";
import { EventFeedbackCard } from "./EventFeedbackCard";
import { EventNotesCard } from "./EventNotesCard";
import { BadgerFlyerCard } from "./BadgerFlyerCard";
import { RideInfoCard } from "./RideInfoCard";
import { RideScheduleCard } from "./RideScheduleCard";
import { RideAttendeesCard } from "./RideAttendeesCard";
import { RideAssetsCard } from "./RideAssetsCard";
import { EventIncidentsCard } from "./EventIncidentsCard";
import { EditEventDialog } from "./EditEventDialog";
import { EventDetailSubNav } from "@/components/layout/EventDetailSubNav";
import { useScrollToHash } from "@/hooks/useScrollToHash";
import { ArrowLeft, ChevronDown, Copy, Pencil, Trash2 } from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { EventType, EventStatus } from "@satyrsmc/shared/lib/enums";
import type { EventAssignmentCategory } from "@satyrsmc/shared/client";
import { useConfirm } from "@/components/ui/confirm-dialog";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

export function EventDetailPage() {
  const { id } = useParams<{ id: string }>();
  if (!id) return null;
  return <EventDetailContent id={id} />;
}

function EventDetailContent({ id }: { id: string }) {
  const navigate = useNavigate();
  const { confirm, confirmDialog } = useConfirm();
  const { invalidateEvent } = useInvalidateQueries();
  const event = unwrapSuspenseData(useEventSuspense(id))!;
  const { data: budgets = [] } = useBudgetsOptional();
  const { data: scenarios = [] } = useScenariosOptional();
  const updateEventMutation = useUpdateEvent();
  const deleteEventMutation = useDeleteEvent();
  const addScheduleItemMutation = useEventScheduleItemCreate();
  const updateScheduleItemMutation = useEventScheduleItemUpdate();
  const deleteScheduleItemMutation = useEventScheduleItemDelete();
  const addIncidentMutation = useEventIncidentCreate();
  const updateIncidentMutation = useEventIncidentUpdate();
  const deleteIncidentMutation = useEventIncidentDelete();
  const addAssetMutation = useEventAssetAdd();
  const deleteAssetMutation = useEventAssetDelete();
  const addMilestoneMutation = useEventMilestoneCreate();
  const updateMilestoneMutation = useEventMilestoneUpdate();
  const deleteMilestoneMutation = useEventMilestoneDelete();
  const addMilestoneMemberMutation = useEventMilestoneAddMember();
  const removeMilestoneMemberMutation = useEventMilestoneRemoveMember();
  const addPackingCategoryMutation = useEventPackingCategoryCreate();
  const addPackingItemMutation = useEventPackingItemCreate();
  const updatePackingItemMutation = useEventPackingItemUpdate();
  const deletePackingItemMutation = useEventPackingItemDelete();
  const addVolunteerMutation = useEventVolunteerCreate();
  const deleteVolunteerMutation = useEventVolunteerDelete();
  const createAssignmentMutation = useEventAssignmentCreate();
  const deleteAssignmentMutation = useEventAssignmentDelete();
  const addAssignmentMemberMutation = useEventAssignmentAddMember();
  const removeAssignmentMemberMutation = useEventAssignmentRemoveMember();
  const addPhotoMutation = useEventPhotoAdd();
  const deletePhotoMutation = useEventPhotoDelete();
  const uploadFlyerMutation = useEventBadgerFlyerUpload();
  const deleteFlyerMutation = useEventBadgerFlyerDelete();
  const createEventMutation = useCreateEvent();
  const [editOpen, setEditOpen] = useState(false);
  const [duplicateOpen, setDuplicateOpen] = useState(false);

  const [editName, setEditName] = useState("");
  const [editDescription, setEditDescription] = useState("");
  const [editYear, setEditYear] = useState<number | "">("");
  const [editStartDate, setEditStartDate] = useState("");
  const [editEndDate, setEditEndDate] = useState("");
  const [editRefundCutoffDate, setEditRefundCutoffDate] = useState("");
  const [editEventUrl, setEditEventUrl] = useState("");
  const [editEventUrlText, setEditEventUrlText] = useState("");
  const [editEventLocation, setEditEventLocation] = useState("");
  const [editEventLocationLink, setEditEventLocationLink] = useState("");
  const [editEventLocationEmbed, setEditEventLocationEmbed] = useState("");
  const [editGaTicketCost, setEditGaTicketCost] = useState<string>("");
  const [editDayPassCost, setEditDayPassCost] = useState<string>("");
  const [editMemberCost, setEditMemberCost] = useState<string>("");
  const [editGaTicketsSold, setEditGaTicketsSold] = useState<string>("");
  const [editDayPassesSold, setEditDayPassesSold] = useState<string>("");
  const [editBudgetId, setEditBudgetId] = useState<string>("");
  const [editScenarioId, setEditScenarioId] = useState<string>("");
  const [editPlanningNotes, setEditPlanningNotes] = useState("");
  const [editEventType, setEditEventType] = useState<string>("badger");
  const [editStartLocation, setEditStartLocation] = useState("");
  const [editEndLocation, setEditEndLocation] = useState("");
  const [editFacebookEventUrl, setEditFacebookEventUrl] = useState("");
  const [editPreRideEventId, setEditPreRideEventId] = useState("");
  const [editRideCost, setEditRideCost] = useState<string>("");
  const [editShowOnWebsite, setEditShowOnWebsite] = useState(false);
  const [editMembersOnly, setEditMembersOnly] = useState(false);
  const [editStatus, setEditStatus] = useState<EventStatus>(EventStatus.Draft);
  const [editPosterBase64, setEditPosterBase64] = useState<string | null | undefined>(undefined);

  // Links like the dashboard's Recent Feedback arrive with a #section hash.
  useScrollToHash();

  useEffect(() => {
    setEditName(event.name);
    setEditDescription(event.description ?? "");
    setEditYear(event.year ?? "");
    setEditStartDate(toDateOnly(event.start_date));
    setEditEndDate(toDateOnly(event.end_date));
    setEditRefundCutoffDate(toDateOnly(event.refund_cutoff_date));
    setEditEventUrl(event.event_url ?? "");
    setEditEventUrlText(event.event_url_text ?? "");
    setEditEventLocation(event.event_location ?? "");
    setEditEventLocationLink(event.event_location_link ?? "");
    setEditEventLocationEmbed(event.event_location_embed ?? "");
    setEditGaTicketCost(event.ga_ticket_cost != null ? String(event.ga_ticket_cost) : "");
    setEditDayPassCost(event.day_pass_cost != null ? String(event.day_pass_cost) : "");
    setEditMemberCost(event.member_cost != null ? String(event.member_cost) : "");
    setEditGaTicketsSold(event.ga_tickets_sold != null ? String(event.ga_tickets_sold) : "");
    setEditDayPassesSold(event.day_passes_sold != null ? String(event.day_passes_sold) : "");
    setEditBudgetId(event.budget_id ?? "");
    setEditScenarioId(event.scenario_id ?? "");
    setEditPlanningNotes(event.planning_notes ?? "");
    setEditEventType(event.event_type ?? "badger");
    setEditStartLocation(event.start_location ?? "");
    setEditEndLocation(event.end_location ?? "");
    setEditFacebookEventUrl(event.facebook_event_url ?? "");
    setEditPreRideEventId(event.pre_ride_event_id ?? "");
    setEditRideCost(event.ride_cost != null ? String(event.ride_cost) : "");
    setEditShowOnWebsite(event.show_on_website ?? false);
    setEditMembersOnly(event.members_only ?? false);
    setEditStatus(event.status ?? EventStatus.Draft);
    setEditPosterBase64(undefined);
  }, [event]);

  const refresh = async () => {
    await invalidateEvent(id);
  };

  const handleSaveEdit = async () => {
    await updateEventMutation.mutateAsync({
      id,
      body: {
        name: editName,
        description: editDescription || null,
        year: editYear === "" ? undefined : Number(editYear),
        start_date: editStartDate || undefined,
        end_date: editEndDate || undefined,
        refund_cutoff_date: editRefundCutoffDate || undefined,
        event_url: editEventUrl || null,
        event_url_text: editEventUrlText || null,
        event_location: editEventLocation || null,
        event_location_link: editEventLocationLink || null,
        event_location_embed: extractEmbedUrlFromHtml(editEventLocationEmbed) || null,
        ga_ticket_cost: editGaTicketCost === "" ? null : parseFloat(editGaTicketCost),
        day_pass_cost: editDayPassCost === "" ? null : parseFloat(editDayPassCost),
        member_cost: editMemberCost === "" ? null : parseFloat(editMemberCost),
        ga_tickets_sold: editGaTicketsSold === "" ? undefined : parseFloat(editGaTicketsSold),
        day_passes_sold: editDayPassesSold === "" ? undefined : parseFloat(editDayPassesSold),
        budget_id: editBudgetId || undefined,
        scenario_id: editScenarioId || undefined,
        planning_notes: editPlanningNotes || null,
        event_type: editEventType as EventType,
        start_location: editStartLocation || null,
        end_location: editEndLocation || null,
        facebook_event_url: editFacebookEventUrl || null,
        pre_ride_event_id: editPreRideEventId || null,
        ride_cost: editRideCost === "" ? null : parseFloat(editRideCost),
        show_on_website: editShowOnWebsite,
        members_only: editMembersOnly,
        status: editStatus,
        ...(editPosterBase64 !== undefined ? { poster: editPosterBase64 } : {}),
      },
    });
    setEditOpen(false);
    refresh();
  };

  const handleDuplicate = async () => {
    const newEvent = await createEventMutation.mutateAsync({
      name: `Copy of ${event.name}`,
      event_type: event.event_type,
      description: event.description ?? undefined,
      start_date: toDateOnly(event.start_date) || undefined,
      end_date: toDateOnly(event.end_date) || undefined,
      event_location: event.event_location ?? undefined,
      event_location_link: event.event_location_link ?? undefined,
      event_location_embed: event.event_location_embed ?? undefined,
      event_url: event.event_url ?? undefined,
      event_url_text: event.event_url_text ?? undefined,
      ga_ticket_cost: event.ga_ticket_cost ?? undefined,
      day_pass_cost: event.day_pass_cost ?? undefined,
      members_only: event.members_only,
      start_location: event.start_location ?? undefined,
      end_location: event.end_location ?? undefined,
      host_ids: event.host_ids && event.host_ids.length > 0 ? event.host_ids : undefined,
    } as Record<string, unknown>);
    setDuplicateOpen(false);
    navigate(`/admin/events/${newEvent.id}`);
  };

  const handleAddScheduleItem = async (body: {
    scheduled_time: string;
    label: string;
    location?: string;
  }) => {
    await addScheduleItemMutation.mutateAsync({ eventId: id, body });
    refresh();
  };

  const handleUpdateScheduleItem = async (
    scheduleId: string,
    body: { scheduled_time?: string; label?: string; location?: string | null },
  ) => {
    await updateScheduleItemMutation.mutateAsync({ eventId: id, scheduleId, body });
    refresh();
  };

  const handleDeleteScheduleItem = async (scheduleId: string) => {
    await deleteScheduleItemMutation.mutateAsync({ eventId: id, scheduleId });
    refresh();
  };

  const handleAddIncident = async (payload: {
    type: string;
    severity: string;
    summary: string;
    details?: string;
    occurred_at?: string;
  }) => {
    await addIncidentMutation.mutateAsync({ eventId: id, body: payload });
    refresh();
  };

  const handleUpdateIncident = async (
    incidentId: string,
    payload: {
      type?: string;
      severity?: string;
      summary?: string;
      details?: string;
      occurred_at?: string | null;
    },
  ) => {
    await updateIncidentMutation.mutateAsync({
      eventId: id,
      incidentId,
      body: payload,
    });
    refresh();
  };

  const handleDeleteIncident = async (incidentId: string) => {
    await deleteIncidentMutation.mutateAsync({ eventId: id, incidentId });
    refresh();
  };

  const handleAddAsset = async (file: File) => {
    await addAssetMutation.mutateAsync({ eventId: id, file });
    refresh();
  };

  const handleDeleteAsset = async (assetId: string) => {
    await deleteAssetMutation.mutateAsync({ eventId: id, assetId });
    refresh();
  };

  const handleAddMilestone = async (payload: {
    month: number;
    year: number;
    description: string;
    due_date: string;
  }) => {
    await addMilestoneMutation.mutateAsync({ eventId: id, body: payload });
    refresh();
  };

  const handleToggleMilestoneComplete = async (mid: string, completed: boolean) => {
    await updateMilestoneMutation.mutateAsync({
      eventId: id,
      mid,
      body: { completed },
    });
    refresh();
  };

  const handleDeleteMilestone = async (mid: string) => {
    await deleteMilestoneMutation.mutateAsync({ eventId: id, mid });
    refresh();
  };

  const handleEditMilestone = async (
    mid: string,
    payload: { month: number; year: number; description: string; due_date: string },
  ) => {
    await updateMilestoneMutation.mutateAsync({
      eventId: id,
      mid,
      body: payload,
    });
    refresh();
  };

  const handleAddMemberToMilestone = async (mid: string, memberId: string) => {
    await addMilestoneMemberMutation.mutateAsync({
      eventId: id,
      mid,
      memberId,
    });
    refresh();
  };

  const handleRemoveMemberFromMilestone = async (mid: string, memberId: string) => {
    await removeMilestoneMemberMutation.mutateAsync({
      eventId: id,
      mid,
      memberId,
    });
    refresh();
  };

  const handleAddPackingCategory = async (name: string) => {
    await addPackingCategoryMutation.mutateAsync({ eventId: id, body: { name } });
    refresh();
  };

  const handleAddPackingItem = async (payload: {
    category_id: string;
    name: string;
    quantity?: number;
    note?: string;
  }) => {
    await addPackingItemMutation.mutateAsync({ eventId: id, body: payload });
    refresh();
  };

  const handleEditPackingItem = async (
    pid: string,
    payload: { category_id?: string; name?: string; quantity?: number; note?: string },
  ) => {
    await updatePackingItemMutation.mutateAsync({
      eventId: id,
      pid,
      body: payload,
    });
    refresh();
  };

  const handleTogglePackingLoaded = async (pid: string, loaded: boolean) => {
    await updatePackingItemMutation.mutateAsync({
      eventId: id,
      pid,
      body: { loaded },
    });
    refresh();
  };

  const handleDeletePackingItem = async (pid: string) => {
    await deletePackingItemMutation.mutateAsync({ eventId: id, pid });
    refresh();
  };

  const handleAddVolunteer = async (payload: { name: string; department: string }) => {
    await addVolunteerMutation.mutateAsync({ eventId: id, body: payload });
    refresh();
  };

  const handleDeleteVolunteer = async (vid: string) => {
    await deleteVolunteerMutation.mutateAsync({ eventId: id, vid });
    refresh();
  };

  const handleCreateRole = async (payload: { name: string; category: EventAssignmentCategory }) => {
    await createAssignmentMutation.mutateAsync({ eventId: id, body: payload });
    refresh();
  };

  const handleDeleteRole = async (aid: string) => {
    await deleteAssignmentMutation.mutateAsync({ eventId: id, aid });
    refresh();
  };

  const handleAddMemberToRole = async (aid: string, memberId: string) => {
    await addAssignmentMemberMutation.mutateAsync({
      eventId: id,
      aid,
      memberId,
    });
    refresh();
  };

  const handleRemoveMemberFromRole = async (aid: string, memberId: string) => {
    await removeAssignmentMemberMutation.mutateAsync({
      eventId: id,
      aid,
      memberId,
    });
    refresh();
  };

  const handleAddPhoto = async (file: File) => {
    await addPhotoMutation.mutateAsync({ eventId: id, file });
    refresh();
  };

  const handleDeletePhoto = async (photoId: string) => {
    await deletePhotoMutation.mutateAsync({ eventId: id, photoId });
    refresh();
  };

  const handleUploadFlyer = async (file: File) => {
    await uploadFlyerMutation.mutateAsync({ eventId: id, file });
    refresh();
  };

  const handleDeleteFlyer = async () => {
    await deleteFlyerMutation.mutateAsync({ eventId: id });
    refresh();
  };

  const handleDeleteEvent = async () => {
    if (!(await confirm(`Delete "${event.name}"? This cannot be undone.`))) return;
    await deleteEventMutation.mutateAsync(id);
    navigate("/admin/events");
  };

  const mapEmbedUrl = event.event_location_embed ?? getMapEmbedUrl(event.event_location);
  const budgetName = budgets.find((b) => b.id === event.budget_id)?.name;
  const scenarioName = scenarios.find((s) => s.id === event.scenario_id)?.name;

  return (
    <div className="space-y-6">
      <EventDetailSubNav eventType={event.event_type} />
      <div className="flex items-center gap-4">
        <Button variant="ghost" size="icon" onClick={() => navigate("/admin/events")}>
          <ArrowLeft className="size-4" />
        </Button>
        <div className="flex-1">
          <h1 className="text-2xl font-bold tracking-tight">{event.name}</h1>
          {event.year != null && <p className="text-muted-foreground text-sm">{event.year}</p>}
        </div>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="outline">
              Actions
              <ChevronDown className="size-4 ml-2" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuItem onClick={() => setEditOpen(true)}>
              <Pencil className="size-4" />
              Edit Event
            </DropdownMenuItem>
            <DropdownMenuItem onClick={() => setDuplicateOpen(true)}>
              <Copy className="size-4" />
              Duplicate
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem
              className="text-destructive focus:text-destructive"
              onClick={handleDeleteEvent}
            >
              <Trash2 className="size-4" />
              Delete
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>

      <Dialog open={duplicateOpen} onOpenChange={setDuplicateOpen}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>Duplicate event</DialogTitle>
            <DialogDescription>
              Create a copy of &ldquo;{event.name}&rdquo;? The new event will be named &ldquo;Copy
              of {event.name}&rdquo; and will open in the admin editor.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDuplicateOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleDuplicate} disabled={createEventMutation.isPending}>
              {createEventMutation.isPending ? "Duplicating..." : "Duplicate"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <div className="grid gap-6 md:grid-cols-2">
        <EventDetailsCard event={event} budgetName={budgetName} scenarioName={scenarioName} />
        {event.event_type === EventType.Rides && <RideInfoCard event={event} />}
        {mapEmbedUrl && <EventLocationCard mapEmbedUrl={mapEmbedUrl} />}
      </div>

      <div className="grid gap-6 md:grid-cols-2">
        <RideAttendeesCard eventId={id} attendees={event.event_attendees ?? []} />
      </div>

      <RideScheduleCard
        eventId={id}
        eventStartDate={event.start_date}
        eventEndDate={event.end_date}
        items={event.ride_schedule_items ?? []}
        onAdd={handleAddScheduleItem}
        onUpdate={handleUpdateScheduleItem}
        onDelete={handleDeleteScheduleItem}
      />
      <RideAssetsCard
        eventId={id}
        eventName={event.name}
        assets={event.event_assets ?? []}
        onAdd={handleAddAsset}
        onDelete={handleDeleteAsset}
      />
      <EventIncidentsCard
        incidents={event.incidents ?? []}
        onAddIncident={handleAddIncident}
        onUpdateIncident={handleUpdateIncident}
        onDeleteIncident={handleDeleteIncident}
      />

      {event.event_type !== EventType.Rides && (
        <>
          <EventMilestonesCard
            event={event}
            onRefresh={refresh}
            onToggleComplete={handleToggleMilestoneComplete}
            onDelete={handleDeleteMilestone}
            onAdd={handleAddMilestone}
            onEdit={handleEditMilestone}
            onAddMember={handleAddMemberToMilestone}
            onRemoveMember={handleRemoveMemberFromMilestone}
          />
          <EventAssignmentsCard
            event={event}
            onCreateRole={handleCreateRole}
            onDeleteRole={handleDeleteRole}
            onAddMember={handleAddMemberToRole}
            onRemoveMember={handleRemoveMemberFromRole}
          />
          <EventPackingCard
            event={event}
            onAddCategory={handleAddPackingCategory}
            onAddItem={handleAddPackingItem}
            onEditItem={handleEditPackingItem}
            onToggleLoaded={handleTogglePackingLoaded}
            onDeleteItem={handleDeletePackingItem}
          />
          <EventVolunteersCard
            event={event}
            onDelete={handleDeleteVolunteer}
            onAdd={handleAddVolunteer}
          />
        </>
      )}

      {event.event_type === EventType.Badger && (
        <BadgerFlyerCard
          eventId={id}
          flyerUrl={event.flyer_url}
          onUpload={handleUploadFlyer}
          onDelete={handleDeleteFlyer}
        />
      )}

      <EventPhotosCard
        eventId={id}
        eventName={event.name}
        photos={event.event_photos ?? []}
        onAddPhoto={handleAddPhoto}
        onDeletePhoto={handleDeletePhoto}
      />

      <EventFeedbackCard eventId={id} />

      <EventNotesCard planningNotes={event.planning_notes} />

      <EditEventDialog
        open={editOpen}
        onOpenChange={setEditOpen}
        editName={editName}
        setEditName={setEditName}
        editDescription={editDescription}
        setEditDescription={setEditDescription}
        editYear={editYear}
        setEditYear={setEditYear}
        editStartDate={editStartDate}
        setEditStartDate={setEditStartDate}
        editEndDate={editEndDate}
        setEditEndDate={setEditEndDate}
        editRefundCutoffDate={editRefundCutoffDate}
        setEditRefundCutoffDate={setEditRefundCutoffDate}
        editEventUrl={editEventUrl}
        setEditEventUrl={setEditEventUrl}
        editEventUrlText={editEventUrlText}
        setEditEventUrlText={setEditEventUrlText}
        editEventLocation={editEventLocation}
        setEditEventLocation={setEditEventLocation}
        editEventLocationLink={editEventLocationLink}
        setEditEventLocationLink={setEditEventLocationLink}
        editEventLocationEmbed={editEventLocationEmbed}
        setEditEventLocationEmbed={setEditEventLocationEmbed}
        editGaTicketCost={editGaTicketCost}
        setEditGaTicketCost={setEditGaTicketCost}
        editDayPassCost={editDayPassCost}
        setEditDayPassCost={setEditDayPassCost}
        editMemberCost={editMemberCost}
        setEditMemberCost={setEditMemberCost}
        editGaTicketsSold={editGaTicketsSold}
        setEditGaTicketsSold={setEditGaTicketsSold}
        editDayPassesSold={editDayPassesSold}
        setEditDayPassesSold={setEditDayPassesSold}
        editBudgetId={editBudgetId}
        setEditBudgetId={setEditBudgetId}
        editScenarioId={editScenarioId}
        setEditScenarioId={setEditScenarioId}
        editPlanningNotes={editPlanningNotes}
        setEditPlanningNotes={setEditPlanningNotes}
        editEventType={editEventType}
        setEditEventType={setEditEventType}
        editStartLocation={editStartLocation}
        setEditStartLocation={setEditStartLocation}
        editEndLocation={editEndLocation}
        setEditEndLocation={setEditEndLocation}
        editFacebookEventUrl={editFacebookEventUrl}
        setEditFacebookEventUrl={setEditFacebookEventUrl}
        editPreRideEventId={editPreRideEventId}
        setEditPreRideEventId={setEditPreRideEventId}
        editRideCost={editRideCost}
        setEditRideCost={setEditRideCost}
        editShowOnWebsite={editShowOnWebsite}
        setEditShowOnWebsite={setEditShowOnWebsite}
        editMembersOnly={editMembersOnly}
        setEditMembersOnly={setEditMembersOnly}
        editStatus={editStatus}
        setEditStatus={setEditStatus}
        existingPosterUrl={event.poster_url ?? null}
        editPosterBase64={editPosterBase64}
        setEditPosterBase64={setEditPosterBase64}
        budgets={budgets}
        scenarios={scenarios}
        onSave={handleSaveEdit}
      />
      {confirmDialog}
    </div>
  );
}
